(() => {
  'use strict';
  if (window.cloAccessibilityLoaded) return;
  window.cloAccessibilityLoaded = true;

  const focusSelector = 'a[href], button, input:not([type="hidden"]), select, textarea, summary, [tabindex]';
  const desktop = window.matchMedia('(min-width: 1025px)');
  const dialogSelector = 'dialog, [role="dialog"], .halo-popup, .halo-sidebar, #halo-sidebar, sticky-add-to-cart, shopify-chat';
  const cartSelector = `#halo-cart-sidebar, [data-cart-sidebar], .mobileMenu-toggle, [data-search-mobile], [data-sidebar], #show-sticky-product, ${dialogSelector}`;
  const mobileDialogs = [
    { id: 'navigation-mobile', className: 'menu_open', trigger: '.mobileMenu-toggle', close: '[data-menu-close-sidebar]' },
    { id: 'search-form-mobile', className: 'open_search_mobile', trigger: '[data-search-mobile]', close: '[data-search-close-sidebar]' },
    { id: 'halo-sidebar', className: 'open-mobile-sidebar', trigger: '[data-sidebar]', close: '[data-close-sidebar]', desktopContent: true },
    { id: 'clo-sticky-options', selector: 'sticky-add-to-cart.style-1[data-sticky-add-to-cart]', className: 'show-mobile-options', trigger: '#show-sticky-product', close: '[data-close-sticky-mobile]', persistentContent: true, title: '.sticky-product-mobile .sticky-title' }
  ];
  let cart = null;
  let cartOpen = false;
  let cartActive = false;
  let cartOpener = null;
  let pendingOpener = null;
  let lastOutsideFocus = null;
  let scheduled = false;
  let cartDirty = false;
  let menusDirty = false;
  let dialogRoots = [];
  let navigationRoots = [];
  let menuId = 0;

  function setAttribute(element, name, value) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }

  function visible(element, ignoreOwnAria = false) {
    if (!element || !element.isConnected || !element.getClientRects().length) return false;
    for (let current = element; current; current = current.parentElement) {
      if (current.hidden || current.hasAttribute('inert') ||
          (current.getAttribute('aria-hidden') === 'true' && !(ignoreOwnAria && current === element))) return false;
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || style.opacity === '0') return false;
    }
    return true;
  }

  function usable(element) {
    return visible(element) && !element.matches(':disabled, [aria-disabled="true"]');
  }

  function revealPaginationFocus(element) {
    if (!element.matches('#CollectionProductGrid a.pagination__item[href]:focus-visible')) return;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      if (document.activeElement !== element || !usable(element) || !element.matches(':focus-visible')) return;
      const target = element.getBoundingClientRect();
      let headerBottom = 0;
      let toolbarBottom = 0;
      let toolbarHeight = 0;
      document.querySelectorAll('.shopify-section-header-sticky, toolbar-item.show-sticky').forEach(chrome => {
        if (!visible(chrome)) return;
        const style = window.getComputedStyle(chrome);
        if (style.position !== 'fixed' && style.position !== 'sticky') return;
        const bounds = chrome.getBoundingClientRect();
        if (!bounds.height || !bounds.width || bounds.right <= target.left || bounds.left >= target.right) return;
        const bottom = Math.max(bounds.bottom, Math.max(0, parseFloat(style.top) || 0) + bounds.height);
        if (chrome.matches('toolbar-item')) {
          toolbarBottom = Math.max(toolbarBottom, bottom);
          toolbarHeight = Math.max(toolbarHeight, bounds.height);
        } else {
          headerBottom = Math.max(headerBottom, bottom);
        }
      });
      const chromeBottom = Math.max(toolbarBottom, headerBottom + toolbarHeight);
      if (!chromeBottom) return;
      const clearTop = chromeBottom + 8;
      if (target.top < clearTop && clearTop + target.height + 8 <= window.innerHeight) {
        window.scrollBy({ top: target.top - clearTop, left: 0, behavior: 'instant' });
      }
    }));
  }

  function focusable(container) {
    return Array.from(container.querySelectorAll(focusSelector)).filter(element => element.tabIndex >= 0 && usable(element));
  }

  function focus(element) {
    if (usable(element)) element.focus({ preventScroll: true });
  }

  function onScreen(element, ignorePointerEvents = false) {
    if (!visible(element, true) || (!ignorePointerEvents && window.getComputedStyle(element).pointerEvents === 'none')) return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.bottom > 0 &&
      rect.left < window.innerWidth && rect.top < window.innerHeight;
  }

  function mobileRoot(state) {
    return state.selector ? document.querySelector(state.selector) : document.getElementById(state.id);
  }

  function mobileRendered(state, root = state.root) {
    return onScreen(root) && (!state.persistentContent || onScreen(root.querySelector('.sticky-product-mobile')));
  }

  function topSidebar() {
    const roots = [];
    if (cart && document.body.classList.contains('cart-sidebar-show') && onScreen(cart)) roots.push(cart);
    if (!desktop.matches) mobileDialogs.forEach(state => {
      const root = mobileRoot(state);
      if (document.body.classList.contains(state.className) && mobileRendered(state, root)) roots.push(root);
    });
    return roots.sort((a, b) => {
      const difference = (parseInt(window.getComputedStyle(a).zIndex, 10) || 0) - (parseInt(window.getComputedStyle(b).zIndex, 10) || 0);
      return difference || (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
    }).pop();
  }

  function otherDialogVisible(owner = cart) {
    if (Array.from(document.querySelectorAll('shopify-chat[open]')).some(host => visible(host) && onScreen(host, true))) return true;
    const top = topSidebar();
    if (top && top !== owner) return true;
    const managed = [cart, ...(!desktop.matches ? mobileDialogs.map(mobileRoot) : [])].filter(Boolean);
    return Array.from(document.querySelectorAll('dialog[open], [role="dialog"][aria-modal="true"], .halo-popup, .halo-sidebar')).some(element => {
      if (element === owner || element.contains(owner) || managed.includes(element) || !visible(element)) return false;
      if (!desktop.matches && element.matches('.search-modal') && document.getElementById('search-form-mobile')?.contains(element)) return false;
      return onScreen(element);
    });
  }

  function activeMobileDialog() {
    if (desktop.matches) return null;
    const top = topSidebar();
    return mobileDialogs.find(state => state.root === top && !otherDialogVisible(top)) || null;
  }

  function withinMobileMenu(element, container) {
    const bounds = container.getBoundingClientRect();
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.right > Math.max(0, bounds.left) &&
      rect.left < Math.min(window.innerWidth, bounds.right);
  }

  function mobilePanel(state) {
    if (state.id !== 'navigation-mobile') return state.root;
    const panels = Array.from(state.root.querySelectorAll('.is-open > .menu-dropdown, .is-open > .list-menu--disclosure-1, .is-open > .site-nav-list-dropdown > .list-menu--disclosure-2, .is-open > .site-nav-sub'));
    return panels.filter(panel => usable(panel) && withinMobileMenu(panel, state.root))
      .reduce((deepest, panel) => deepest.contains(panel) ? panel : deepest, state.root);
  }

  function mobileFocusable(state) {
    const panel = mobilePanel(state);
    const close = state.root.querySelector(state.close);
    return focusable(state.root).filter(element => element === close || (panel.contains(element) &&
      (state.id !== 'navigation-mobile' || (withinMobileMenu(element, panel) && withinMobileMenu(element, state.root)))));
  }

  function mobileTabStops(state) {
    const elements = mobileFocusable(state);
    return elements.filter(element => {
      if (!element.matches('input[type="radio"][name]')) return true;
      const group = elements.filter(other => other.matches('input[type="radio"]') && other.name === element.name && other.form === element.form);
      return element === (group.find(other => other.checked) || group[0]);
    });
  }

  function focusMobile(state) {
    const elements = mobileFocusable(state);
    const preferred = state.root.querySelector(state.id === 'search-form-mobile' ? 'input[type="search"]' : state.close);
    focus(elements.includes(preferred) ? preferred : elements[0] || state.root);
  }

  function prepareMobile(state) {
    const root = state.root;
    if (state.semanticsRoot !== root) {
      state.semanticsRoot = root;
      if (root.getAttribute('data-clo-dialog') !== state.id || !state.originalSemantics) {
        state.originalSemantics = Object.fromEntries(['role', 'tabindex', 'aria-hidden', 'aria-modal', 'aria-labelledby'].map(name => [name, root.getAttribute(name)]));
      }
    }
    if (!root.id) root.id = state.id;
    setAttribute(root, 'data-clo-dialog', state.id);
    setAttribute(root, 'role', 'dialog');
    setAttribute(root, 'tabindex', '-1');
    const title = root.querySelector(state.title || '.halo-sidebar-header .title');
    if (title && !root.hasAttribute('aria-label') && !root.hasAttribute('aria-labelledby')) {
      if (!title.id) title.id = `clo-${state.id}-title`;
      setAttribute(root, 'aria-labelledby', title.id);
    }
    root.querySelectorAll('.nav-title-mobile:not(a):not(button)').forEach(back => {
      setAttribute(back, 'role', 'button');
      setAttribute(back, 'tabindex', '0');
      setAttribute(back, 'data-clo-mobile-back', '');
    });
  }

  function syncMobileDialogs() {
    mobileDialogs.forEach(state => {
      const previousRoot = state.root;
      const wasOpen = state.open;
      state.root = mobileRoot(state);
      if (state.root && !state.root.id) state.root.id = state.id;
      state.open = Boolean(state.root && !desktop.matches && document.body.classList.contains(state.className));
      if (state.open && !wasOpen) state.opener = state.pendingOpener || (document.activeElement !== document.body ? document.activeElement : lastOutsideFocus);
      state.pendingOpener = null;
      if (state.root) {
        if ((state.persistentContent && (!state.open || !mobileRendered(state))) || (state.desktopContent && desktop.matches)) {
          if (state.semanticsRoot === state.root) {
            Object.entries(state.originalSemantics).forEach(([name, value]) => {
              if (value === null) state.root.removeAttribute(name);
              else setAttribute(state.root, name, value);
            });
            state.root.removeAttribute('data-clo-dialog');
          }
        } else {
          prepareMobile(state);
          setAttribute(state.root, 'aria-hidden', String(!state.open || !mobileRendered(state)));
          setAttribute(state.root, 'aria-modal', String(activeMobileDialog() === state));
        }
      }
      document.querySelectorAll(state.trigger).forEach(trigger => {
        if (state.root) {
          state.controlId = state.root.id || state.id;
          setAttribute(trigger, 'aria-controls', state.controlId);
          setAttribute(trigger, 'aria-haspopup', 'dialog');
        } else if (trigger.getAttribute('aria-controls') === (state.controlId || state.id)) {
          trigger.removeAttribute('aria-controls');
          trigger.removeAttribute('aria-haspopup');
        }
        setAttribute(trigger, 'aria-expanded', String(state.open));
        if (state.id === 'halo-sidebar' && !trigger.matches('a, button, input')) {
          setAttribute(trigger, 'role', 'button');
          setAttribute(trigger, 'tabindex', '0');
          setAttribute(trigger, 'data-clo-mobile-action', '');
        }
      });
      if (wasOpen && !state.open) {
        const current = document.activeElement;
        if ((current === document.body || previousRoot?.contains(current)) && !otherDialogVisible(previousRoot)) {
          const fallback = Array.from(document.querySelectorAll(state.trigger)).find(usable);
          focus(usable(state.opener) ? state.opener : fallback);
        }
        state.opener = null;
      }
    });
    const active = activeMobileDialog();
    if (!active) return;
    const elements = mobileFocusable(active);
    if (active.pendingFocus && elements.includes(active.pendingFocus)) focus(active.pendingFocus);
    else if (!active.root.contains(document.activeElement) || !usable(document.activeElement) ||
        (document.activeElement !== active.root && !elements.includes(document.activeElement))) focusMobile(active);
    active.pendingFocus = null;
  }

  function activeCart() {
    return cart && document.body.classList.contains('cart-sidebar-show') && visible(cart, true) &&
      window.getComputedStyle(cart).pointerEvents !== 'none' && !otherDialogVisible();
  }

  function focusCart() {
    const close = cart.querySelector('[data-close-cart-sidebar]');
    focus(usable(close) ? close : focusable(cart)[0] || cart);
  }

  function prepareCart() {
    if (!cart) return;
    setAttribute(cart, 'role', 'dialog');
    setAttribute(cart, 'tabindex', '-1');
    const title = cart.querySelector('.halo-sidebar-header .title');
    if (title && !cart.hasAttribute('aria-label') && !cart.hasAttribute('aria-labelledby')) {
      if (!title.id) title.id = 'clo-cart-title';
      setAttribute(cart, 'aria-labelledby', title.id);
    }
    cart.querySelectorAll('.previewCart-emptyBody a.button-continue:not([href])[onclick]').forEach(button => {
      setAttribute(button, 'role', 'button');
      setAttribute(button, 'tabindex', '0');
      button.removeAttribute('aria-disabled');
      setAttribute(button, 'data-clo-cart-action', '');
    });
  }

  function syncCart() {
    dialogRoots = Array.from(document.querySelectorAll(dialogSelector));
    const previousCart = cart;
    cart = document.getElementById('halo-cart-sidebar');
    const open = Boolean(cart && document.body.classList.contains('cart-sidebar-show'));
    prepareCart();
    if (open && !cartOpen) {
      cartOpener = pendingOpener || (document.activeElement !== document.body ? document.activeElement : lastOutsideFocus);
      pendingOpener = null;
    }
    const wasOpen = cartOpen;
    cartOpen = open;
    if (cart) {
      const rendered = open && visible(cart, true);
      setAttribute(cart, 'aria-hidden', String(!rendered));
      cartActive = Boolean(activeCart());
      setAttribute(cart, 'aria-modal', String(cartActive));
      document.querySelectorAll('[data-cart-sidebar]').forEach(trigger => {
        setAttribute(trigger, 'aria-controls', cart.id);
        setAttribute(trigger, 'aria-expanded', String(open));
      });
    } else {
      cartActive = false;
      document.querySelectorAll('[data-cart-sidebar][aria-controls="halo-cart-sidebar"]').forEach(trigger => {
        trigger.removeAttribute('aria-controls');
        setAttribute(trigger, 'aria-expanded', 'false');
      });
    }
    if (cartActive && (!cart.contains(document.activeElement) || !usable(document.activeElement))) focusCart();
    if (wasOpen && !open) {
      const current = document.activeElement;
      if ((current === document.body || previousCart?.contains(current)) && !otherDialogVisible()) {
        const fallback = Array.from(document.querySelectorAll('[data-cart-sidebar]')).find(usable);
        focus(usable(cartOpener) ? cartOpener : fallback);
      }
      cartOpener = null;
      pendingOpener = null;
    }
  }

  function menuParts(item) {
    const wrapper = Array.from(item.children).find(child => child.classList.contains('site-nav-list-dropdown')) || item;
    return {
      trigger: Array.from(wrapper.children).find(child => child.matches('a.header__menu-item')),
      submenu: Array.from(wrapper.children).find(child => child.matches('.header__submenu, .menu-dropdown'))
    };
  }

  function menuItems() {
    return Array.from(document.querySelectorAll('.header__inline-menu .menu-lv-item'));
  }

  function syncMenus() {
    navigationRoots = Array.from(document.querySelectorAll('.header__inline-menu'));
    menuItems().forEach(item => {
      const { trigger, submenu } = menuParts(item);
      if (!trigger || !submenu) return;
      if (!desktop.matches || item.closest('#navigation-mobile, .site-nav-mobile')) {
        if (item.hasAttribute('data-clo-menu')) {
          item.removeAttribute('data-clo-menu');
          item.removeAttribute('data-clo-menu-state');
          trigger.removeAttribute('aria-controls');
          trigger.removeAttribute('aria-expanded');
        }
        return;
      }
      if (!submenu.id) {
        do { menuId += 1; } while (document.getElementById(`clo-navigation-${menuId}`));
        submenu.id = `clo-navigation-${menuId}`;
      }
      setAttribute(item, 'data-clo-menu', '');
      setAttribute(trigger, 'aria-controls', submenu.id);
      setAttribute(trigger, 'aria-expanded', String(visible(submenu)));
    });
  }

  function openMenu(item) {
    if (!desktop.matches || !item?.hasAttribute('data-clo-menu')) return;
    setAttribute(item, 'data-clo-menu-state', 'open');
    setAttribute(menuParts(item).trigger, 'aria-expanded', 'true');
  }

  function closeMenusOutside(target) {
    menuItems().forEach(item => {
      if (!item.contains(target)) item.removeAttribute('data-clo-menu-state');
    });
    syncMenus();
  }

  function schedule(cartChanged, menusChanged) {
    cartDirty = cartDirty || cartChanged;
    menusDirty = menusDirty || menusChanged;
    if (scheduled || (!cartDirty && !menusDirty)) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      const updateCart = cartDirty;
      const updateMenus = menusDirty;
      cartDirty = false;
      menusDirty = false;
      if (updateCart) {
        syncCart();
        syncMobileDialogs();
      }
      if (updateMenus) syncMenus();
    });
  }

  function affected(element) {
    if (!(element instanceof Element)) return [false, false];
    return [
      Boolean(element.closest(cartSelector) || (cart && element.contains(cart)) || dialogRoots.some(root => element.contains(root))),
      Boolean(element.closest('.header__inline-menu') || navigationRoots.some(root => element.contains(root)))
    ];
  }

  function observeChanges(records) {
    let updateCart = false;
    let updateMenus = false;
    records.forEach(record => {
      if (record.type === 'attributes' && record.target === document.body && record.attributeName === 'class') {
        updateCart = true;
        const previouslyMobile = /(?:^|\s)menu_open(?:\s|$)/.test(record.oldValue || '');
        updateMenus = updateMenus || previouslyMobile !== document.body.classList.contains('menu_open');
        return;
      }
      const [cartChanged, menusChanged] = record.type === 'childList' && record.target === document.body ? [false, false] : affected(record.target);
      updateCart = updateCart || cartChanged;
      updateMenus = updateMenus || menusChanged;
      if (record.type === 'childList') {
        [...record.addedNodes, ...record.removedNodes].forEach(node => {
          if (!(node instanceof Element)) return;
          updateCart = updateCart || node.matches(cartSelector) || Boolean(node.querySelector(cartSelector));
          updateMenus = updateMenus || node.matches('.header__inline-menu') || Boolean(node.querySelector('.header__inline-menu'));
        });
      }
    });
    schedule(updateCart, updateMenus);
  }

  function initialize() {
    const style = document.createElement('style');
    style.id = 'clo-keyboard-navigation';
    const targets = [' > .header__submenu', ' > .menu-dropdown', ' > .site-nav-list-dropdown > .header__submenu'];
    const selectors = state => targets.map(target => `.header__inline-menu [data-clo-menu-state="${state}"]${target}`).join(',');
    style.textContent = `@media (min-width: 1025px) {${selectors('open')} {display:block!important;opacity:1;visibility:visible;pointer-events:auto;transform:translateY(0);z-index:2} ${selectors('closed')} {display:none!important}}`;
    document.head.appendChild(style);

    document.addEventListener('click', event => {
      mobileDialogs.forEach(state => {
        const opener = event.target.closest(state.trigger);
        if (opener && !state.open) {
          state.pendingOpener = opener;
          window.requestAnimationFrame(() => {
            if (!document.body.classList.contains(state.className)) state.pendingOpener = null;
          });
        }
      });
      const mobile = activeMobileDialog();
      const navigation = event.target.closest('.menu_mobile_link, .menu_mobile_link_2, .nav-title-mobile');
      if (mobile?.id === 'navigation-mobile' && mobile.root.contains(navigation) &&
          (event.detail === 0 || document.activeElement === navigation)) {
        const back = navigation.matches('.nav-title-mobile');
        const item = navigation.closest('.menu-lv-item');
        const { trigger, submenu } = item ? menuParts(item) : {};
        queueMicrotask(() => {
          prepareMobile(mobile);
          mobile.pendingFocus = back ? trigger : submenu?.querySelector('.nav-title-mobile');
          schedule(true, false);
        });
      }
      const trigger = event.target.closest('[data-cart-sidebar]');
      if (trigger && !cartOpen) {
        pendingOpener = trigger;
        queueMicrotask(() => {
          if (!document.body.classList.contains('cart-sidebar-show')) pendingOpener = null;
        });
      }
    }, true);

    document.addEventListener('focusin', event => {
      const mobile = activeMobileDialog();
      if (mobile && event.target !== mobile.root && !mobileFocusable(mobile).includes(event.target)) {
        focusMobile(mobile);
        return;
      }
      if (!cartOpen && event.target !== document.body && !cart?.contains(event.target)) lastOutsideFocus = event.target;
      if (activeCart() && !cart.contains(event.target)) {
        focusCart();
        return;
      }
      revealPaginationFocus(event.target);
      if (!desktop.matches || !event.target.closest('.header__inline-menu')) return;
      closeMenusOutside(event.target);
      let item = event.target.closest('[data-clo-menu]');
      while (item) {
        if (item.getAttribute('data-clo-menu-state') !== 'closed') openMenu(item);
        item = item.parentElement.closest('[data-clo-menu]');
      }
    });

    document.addEventListener('focusout', event => {
      if (event.target.closest('.header__inline-menu')) window.setTimeout(() => closeMenusOutside(document.activeElement), 0);
    });

    document.addEventListener('keydown', event => {
      if (event.defaultPrevented) return;
      if (!desktop.matches && (event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-clo-mobile-action]')) {
        event.preventDefault();
        if (event.key === 'Enter' && !event.repeat) event.target.click();
        return;
      }
      const mobile = activeMobileDialog();
      if (mobile) {
        if (event.key === 'Escape') {
          const close = mobile.root.querySelector(mobile.close);
          if (usable(close)) {
            event.preventDefault();
            event.stopPropagation();
            close.click();
          }
          return;
        }
        if (event.key === 'Tab') {
          const elements = mobileTabStops(mobile);
          if (mobile.id === 'navigation-mobile' && mobilePanel(mobile) !== mobile.root && elements.length) {
            event.preventDefault();
            const index = elements.indexOf(document.activeElement);
            const next = index < 0 ? (event.shiftKey ? elements.length - 1 : 0) :
              (index + (event.shiftKey ? -1 : 1) + elements.length) % elements.length;
            elements[next].focus();
            return;
          }
          const first = elements[0] || mobile.root;
          const last = elements[elements.length - 1] || mobile.root;
          if (!elements.includes(document.activeElement) ||
              (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
            event.preventDefault();
            focus(event.shiftKey ? last : first);
          }
          return;
        }
        if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-clo-mobile-back]')) {
          event.preventDefault();
          if (event.key === 'Enter' && !event.repeat) event.target.click();
          return;
        }
      }
      if (activeCart()) {
        if (event.key === 'Escape') {
          const close = cart.querySelector('[data-close-cart-sidebar]');
          if (usable(close)) {
            event.preventDefault();
            event.stopPropagation();
            close.click();
          }
          return;
        }
        if (event.key === 'Tab') {
          const elements = focusable(cart);
          const first = elements[0] || cart;
          const last = elements[elements.length - 1] || cart;
          if (!cart.contains(document.activeElement) || document.activeElement === cart ||
              (event.shiftKey ? document.activeElement === first : document.activeElement === last)) {
            event.preventDefault();
            focus(event.shiftKey ? last : first);
          }
          return;
        }
        if ((event.key === 'Enter' || event.key === ' ') && event.target.matches('[data-clo-cart-action]')) {
          event.preventDefault();
          if (event.key === 'Enter' && !event.repeat) event.target.click();
          return;
        }
      }
      if (!desktop.matches) return;
      let item = event.target.closest('[data-clo-menu]');
      if (event.key === 'Escape') {
        while (item && !visible(menuParts(item).submenu)) item = item.parentElement.closest('[data-clo-menu]');
      }
      if (!item) return;
      const { trigger, submenu } = menuParts(item);
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'ArrowRight') && event.target === trigger) {
        event.preventDefault();
        openMenu(item);
        const links = focusable(submenu);
        focus(event.key === 'ArrowUp' ? links[links.length - 1] : links[0]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        item.querySelectorAll('[data-clo-menu-state]').forEach(child => child.removeAttribute('data-clo-menu-state'));
        setAttribute(item, 'data-clo-menu-state', 'closed');
        setAttribute(trigger, 'aria-expanded', 'false');
        focus(trigger);
      }
    });

    document.addEventListener('keyup', event => {
      if (!desktop.matches && event.key === ' ' && event.target.matches('[data-clo-mobile-action]')) {
        event.preventDefault();
        event.target.click();
      }
      if (event.key === ' ' && activeMobileDialog() && event.target.matches('[data-clo-mobile-back]')) {
        event.preventDefault();
        event.target.click();
      }
      if (event.key === ' ' && activeCart() && event.target.matches('[data-clo-cart-action]')) {
        event.preventDefault();
        event.target.click();
      }
    });

    document.addEventListener('mouseover', event => {
      if (!desktop.matches || !event.target.closest('.header__inline-menu')) return;
      const item = event.target.closest('[data-clo-menu]');
      if (item?.getAttribute('data-clo-menu-state') === 'closed' && !item.contains(event.relatedTarget)) item.removeAttribute('data-clo-menu-state');
      schedule(false, true);
    });
    document.addEventListener('mouseout', event => {
      if (event.target.closest('.header__inline-menu')) schedule(false, true);
    });
    document.addEventListener('transitionend', event => schedule(...affected(event.target)), true);
    document.addEventListener('load', event => {
      if (event.target instanceof Element && event.target.matches('link[rel="stylesheet"]')) schedule(true, true);
      else schedule(...affected(event.target));
    }, true);
    window.addEventListener('pageshow', event => {
      if (!event.persisted && window.performance.getEntriesByType?.('navigation')[0]?.type !== 'back_forward') return;
      window.requestAnimationFrame(() => revealPaginationFocus(document.activeElement));
    });
    window.addEventListener('resize', () => schedule(true, true));
    desktop.addEventListener('change', () => schedule(true, true));
    new MutationObserver(observeChanges).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: ['class', 'style', 'hidden', 'inert', 'disabled', 'open', 'aria-hidden', 'aria-modal', 'role']
    });
    syncCart();
    syncMobileDialogs();
    syncMenus();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
